import React, { useEffect, useMemo } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import Authorization from "@routes/providers";
import Loader from "@components/loader";
import InvalidUser from "@routes/errors/InvalidUser";
import { loadCustomMenus } from "@modules/custom-menus";
import { useDispatch, useSelector } from "react-redux";
import { CUSTOM_MENU_DOMAIN, MENU_LIST } from "@constants";
import { siteMode } from "@cores/siteMode";
import Error404 from "@routes/errors/Error404";
import { LayoutForNoting } from "@components/layout";
import { adminSettingsRoutes, defaultPath, projectSettingsRoutes } from "@cores/routes";
import ListSharedV2 from "@routes/shared-v2/list-shared-v2";
import Loadable from "@react-loadable/revised";

const LoadableCollectionDetail = Loadable({
    loader: () => import("@routes/collections/detail-collection").catch((error) => console.error(error)),
    loading: Loader,
});

const LoadableCollectionsSearch = Loadable({
    loader: () => import("@routes/collections/list-search-collections").catch((error) => console.error(error)),
    loading: Loader,
});

const LoadableRequest = Loadable({
    loader: () => import("@routes/request/list-request").catch((error) => console.error(error)),
    loading: Loader,
});

const LoadableRequestDetail = Loadable({
    loader: () => import("@routes/request/detail-request").catch((error) => console.error(error)),
    loading: Loader,
});

const LoadableSearchWithSE = Loadable({
    loader: () => import("@routes/searchWithSE"),
    loading: Loader,
});

const LoadableAssetDetail = Loadable({
    loader: () => import("@routes/asset/Detail"),
    loading: Loader,
});

const LoadableLibraryAssetDetail = Loadable({
    loader: () => import("@routes/asset/detail-library-asset"),
    loading: Loader,
});

const CustomRoutes = () => {
    const dispatch = useDispatch();
    const isCms = siteMode.get() === "CMS";
    const { pathname } = useLocation();

    const customMenus = useSelector(({ customMenus }) => customMenus);
    const projectId = useSelector(({ project }) => project?.id);
    const global = useSelector(({ global }) => global);

    useEffect(() => {
        loadCustomRoutes();
    }, []);

    const loadCustomRoutes = async () => {
        try {
            await dispatch(loadCustomMenus({ isLibrary: isCms }));
        } catch (e) {
            console.error(e);
        }
    };

    const getParsedSearchParams = (domain, searchParams) => {
        if (!searchParams) return {};
        let params = {};
        switch (domain) {
            case CUSTOM_MENU_DOMAIN.ASSETS:
                const { virtualFolder, categories, customMetadata, viewMode } = searchParams;
                if (viewMode) {
                    params.viewMode = JSON.stringify(viewMode);
                }
                if (virtualFolder || categories) {
                    if (virtualFolder) {
                        params.virtualFolderId = virtualFolder.virtualFolderId;
                        params.virtualDriveId = virtualFolder.driveId;
                    }
                    if (categories) {
                        params.selectedCategoryId = categories.categories?.map((category) => category.id)?.join(",");
                        params.defaultCategoryGroupId = categories.categoryGroupId;
                    }
                }
                if (customMetadata) {
                    params.selectedContentSchemaId = customMetadata.id;
                }
                return params;
                break;
            case CUSTOM_MENU_DOMAIN.COLLECTIONS:
                if (searchParams.viewMode) params.viewMode = JSON.stringify(searchParams.viewMode);
                return params;
            default:
                return {};
        }
    };

    const routes = useMemo(() => {
        const rootPath = `${isCms ? "/library" : ""}${defaultPath}`;

        const getAdditionalCommonRoutes = () => {
            return [
                {
                    name: `Asset Detail`,
                    exact: true,
                    path: `${rootPath}/customMenuSets/common/assets/assets/:id`,
                    component: isCms ? LoadableLibraryAssetDetail : LoadableAssetDetail,
                    connectedMenuId: MENU_LIST.ASSETS,
                },
                {
                    name: "Shared v2",
                    exact: true,
                    path: `${rootPath}/share`,
                    component: ListSharedV2,
                },
                {
                    name: "Request",
                    exact: true,
                    path: `${rootPath}/requests`,
                    component: LoadableRequest,
                },
                {
                    name: "Request Detail",
                    exact: true,
                    path: `${rootPath}/requests/:id`,
                    component: LoadableRequestDetail,
                },
                {
                    name: "Search with Search Engine",
                    exact: true,
                    path: `${rootPath}/search/engine`,
                    component: LoadableSearchWithSE,
                },
                ...adminSettingsRoutes,
                ...projectSettingsRoutes,
            ];
        };

        const getAdditionalRoutesByDomain = (path, menu) => {
            let results = [];
            if (menu.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                const routes = [
                    {
                        id: menu.id,
                        name: `Collection Search`,
                        exact: true,
                        path: `${rootPath}/customMenuSets/:customMenuSetId/${menu.id}/search`,
                        component: LoadableCollectionsSearch,
                        connectedMenuId: MENU_LIST.COLLECTIONS,
                    },
                    {
                        id: menu.id,
                        name: `Collection Detail`,
                        exact: true,
                        path: `${rootPath}/customMenuSets/:customMenuSetId/${menu.id}/:id`,
                        component: LoadableCollectionDetail,
                        connectedMenuId: MENU_LIST.COLLECTIONS,
                    },
                ];
                results.push(...routes);
            }
            return results;
        };

        const getConnectedMenuId = (menu) => {
            switch (menu.domain) {
                case CUSTOM_MENU_DOMAIN.ASSETS:
                    return MENU_LIST.ASSETS;
                case CUSTOM_MENU_DOMAIN.COLLECTIONS:
                    return MENU_LIST.COLLECTIONS;
                case CUSTOM_MENU_DOMAIN.DASHBOARD:
                    return MENU_LIST.DASHBOARD;
                case CUSTOM_MENU_DOMAIN.STORAGES:
                    return MENU_LIST.STORAGES;
            }
        };

        const recursiveRoutes = (menus, results, domains) => {
            menus?.map((menu) => {
                let path = `${rootPath}/customMenuSets/:customMenuSetId/${menu.id}`;
                if (menu.type === "DETAIL") path = `${path}/:id`;
                if (!results.some((r) => r.path === path) && menu.domain) {
                    const _menu = {
                        id: menu.id,
                        name: `${menu.domain}_${menu.type}`,
                        exact: true,
                        path,
                        component: menu.component,
                        searchParams: getParsedSearchParams(menu.domain, menu.searchParams),
                        connectedMenuId: getConnectedMenuId(menu),
                    };
                    results.push(_menu);
                    results.push(...getAdditionalRoutesByDomain(path, menu));
                }
                if (menu.subMenus) recursiveRoutes(menu.subMenus, results, domains);
            });
            return results;
        };

        const routes = recursiveRoutes(customMenus?.data, [], []);
        routes.push(...getAdditionalCommonRoutes());

        return routes;
    }, [projectId, customMenus.data, isCms]);

    if (customMenus.pending) {
        return <Loader />;
    }

    if (!global.isAgreementRequired && customMenus.error) {
        // TODO: custom error page 변경
        return <InvalidUser />;
    }

    return (
        <Switch>
            {routes?.map((route) => {
                return (
                    <Route
                        key={route.id ?? route.name}
                        path={route.path}
                        exact={route.exact}
                        render={(props) => {
                            const searchParams = new URLSearchParams();
                            if (route.searchParams) {
                                Object.entries(route.searchParams)?.forEach(([key, value]) => {
                                    searchParams.append(key, value);
                                });
                            }
                            // 기존 props에 searchParams 추가
                            const enhancedProps = {
                                ...props,
                                location: {
                                    ...props.location,
                                    search: searchParams.toString()
                                        ? `?${searchParams.toString()}`
                                        : props.location.search,
                                },
                            };
                            return (
                                <Authorization connectedMenuId={route.connectedMenuId}>
                                    <route.component key={pathname} {...enhancedProps} />
                                </Authorization>
                            );
                        }}
                    />
                );
            })}
            <Route path={"*"} component={Error404} />
        </Switch>
    );
};
export default CustomRoutes;
